// frontend/src/pages/CalendarPage.js
import { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, DatePicker, Select, Typography, Calendar, Badge, Card, Popover, List, Spin, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useAcademicYear } from '../context/AcademicYearContext';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { getAllEvents, createEvent, deleteEvent } from '../services/calendarService';
import useResponsive from '../hooks/useResponsive';
import { useNotification } from '../context/NotificationContext';
dayjs.extend(isBetween);

const { Title } = Typography;
const { RangePicker } = DatePicker;

const CalendarPage = () => {
    const { message } = useNotification();
    const { user } = useAuth();
    const { currentYear } = useAcademicYear();
    const { isMobile } = useResponsive();
    const [events, setEvents] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);

    // --- Data Fetching ---
    useEffect(() => {
        if (currentYear) {
            fetchEvents();
        }
    }, [currentYear]);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const eventData = await getAllEvents();
            setEvents(eventData);
        } catch (error) {
            message.error('Failed to load calendar events.');
        } finally {
            setLoading(false);
        }
    };

    // --- Event Handlers ---
    const handleAddEventClick = () => {
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleModalOk = () => {
        form.validateFields().then(async (values) => {
            const eventData = {
                title: values.title,
                start_date: values.dates[0].format('YYYY-MM-DD'),
                end_date: values.dates[1].format('YYYY-MM-DD'),
                description: values.description,
                event_type: values.event_type,
            };
            try {
                await createEvent(eventData);
                message.success('Event created successfully!');
                setIsModalVisible(false);
                fetchEvents();
            } catch (error) {
                message.error('Failed to create event.');
            }
        });
    };

    const handleDelete = async (eventId, eventTitle) => {
        try {
            await deleteEvent(eventId);
            message.success('Event deleted successfully!');
            fetchEvents(); // Refresh the calendar
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to delete the event.');
        }

    };


    // --- Double Click/Tap Handler ---
    const handleCellDoubleClick = (date) => {
        if (user.role !== 'principal' && user.role !== 'super_admin') return;
        form.resetFields();
        form.setFieldsValue({ dates: [date, date] });
        setIsModalVisible(true);
    };

    // --- Popover and Cell Rendering for DESKTOP ---
    const getPopoverContent = (listData) => (
        <List
            size="small"
            dataSource={listData}
            renderItem={(item) => (
                <List.Item
                    actions={
                        (user && (user.role === 'principal' || user.role === 'super_admin') && item.event_type !== 'exam')
                            // CORRECTED: Use an arrow function to ensure the correct context
                            ? [<Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(item.id, item.title)} />]
                            : []
                    }
                >
                    <List.Item.Meta
                        title={<Badge status={
                            item.event_type === 'exam' ? 'error' :
                                item.event_type === 'holiday' ? 'success' : 'processing'
                        } text={item.title} />}
                    />
                </List.Item>
            )}
        />
    );


    const desktopCellRender = (current, info) => {
        if (info.type !== 'date') return info.originNode;

        const dateStr = current.format('YYYY-MM-DD');
        const dayOfWeek = current.day();
        const workingDays = currentYear?.working_days || [1, 2, 3, 4, 5];
        const isWorkingDay = workingDays.includes(dayOfWeek);
        const isCurrentMonth = current.isSame(info.currentMonth, 'month');

        const eventsOnDate = events.filter(event => dayjs(dateStr).isBetween(event.start, event.end, 'day', '[]'));
        const isHoliday = eventsOnDate.some(e => e.event_type === 'holiday');

        let cellClassName = 'calendar-day-desktop';
        if (!isWorkingDay || isHoliday) cellClassName += ' non-working-day';
        if (!isCurrentMonth) cellClassName += ' other-month';

        return (
            <div className={cellClassName} onDoubleClick={() => handleCellDoubleClick(current)}>
                <div className="day-number">{current.date()}</div>
                <div className="events-container">
                    {eventsOnDate.map(item => (
                        <Popover
                            key={item.id}
                            content={() => getPopoverContent([item])}
                            title="Event Details"
                            trigger="click"
                        >
                            <div className="event-item" onClick={(e) => e.stopPropagation()}>
                                <Badge
                                    status={
                                        item.event_type === 'exam' ? 'error' :
                                            item.event_type === 'holiday' ? 'success' : 'processing'
                                    }
                                    text={item.title}
                                />
                            </div>
                        </Popover>
                    ))}
                </div>
            </div>
        );
    };

    // --- Main render function for switching views ---
    const renderCalendarView = () => {
        if (isMobile) {
            const upcomingEvents = events
                .filter(event => dayjs(event.end).isAfter(dayjs().startOf('day')))
                .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

            return (
                <List
                    itemLayout="vertical"
                    dataSource={upcomingEvents}
                    renderItem={item => {
                        let tagColor = 'default';
                        if (item.event_type === 'exam') tagColor = 'volcano';
                        if (item.event_type === 'holiday') tagColor = 'green';
                        if (item.event_type === 'program') tagColor = 'blue';

                        return (
                            <List.Item
                                actions={(user && (user.role === 'principal' || user.role === 'super_admin') && item.event_type !== 'exam')
                                    ? [<Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(item.id, item.title)}>Delete</Button>]
                                    : []
                                }
                            >
                                <List.Item.Meta
                                    title={<><Tag color={tagColor}>{item.event_type.toUpperCase()}</Tag>{item.title}</>}
                                    description={`${dayjs(item.start).format('MMM D, YYYY')} to ${dayjs(item.end).format('MMM D, YYYY')}`}
                                />
                                {item.description}
                            </List.Item>
                        );
                    }}
                />
            );
        }

        // Use the new cellRender prop for AntD v5+
        return <Calendar cellRender={desktopCellRender} />;
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={2} style={{ margin: 0 }}>Academic Calendar</Title>
                {user && (user.role === 'principal' || user.role === 'super_admin') && (
                    <Button type="default" icon={<PlusOutlined />} onClick={handleAddEventClick}>
                        Add Event
                    </Button>
                )}
            </div>

            <Card>
                {loading ? <div style={{ textAlign: 'center', padding: '50px' }}><Spin /></div> : renderCalendarView()}
            </Card>

            <Modal title="Add New Calendar Event" open={isModalVisible} onOk={handleModalOk} onCancel={() => setIsModalVisible(false)}>
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Event Title" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="dates" label="Start & End Date" rules={[{ required: true }]}>
                        <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="event_type" label="Event Type" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="holiday">Holiday</Select.Option>
                            <Select.Option value="program">School Program</Select.Option>
                            <Select.Option value="other">Other</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CalendarPage;